import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { formatRelativeDate, formatCurrency } from '@/lib/utils'
import { fromAddressPrefix, formatAddress } from '@/lib/address'
import { getQuoteStatusConfig } from '@/pages/payments/quote-status'
import {
  Wrench, FileText, Receipt, TrendingUp, MessageSquare, StickyNote,
  CheckSquare, GitBranch, Filter,
  type LucideIcon,
} from 'lucide-react'

/**
 * ActivityTimeline — unified, chronological view of everything that has
 * happened on a contact or company.
 *
 * Sources merged in:
 *   - field_jobs (created/scheduled)
 *   - quotes (created)
 *   - invoices (created)
 *   - deals (created)
 *   - conversations (last_message_at or created_at)
 *   - entity_notes (created)
 *
 * Contact mode: each source is filtered by `contact_id = entityId` (plus
 * the polymorphic note key). Company mode: source rows can attach via
 * either `company_id = entityId` OR a `contact_id` that belongs to one of
 * the company's contacts — both are unioned client-side after a dependent
 * contacts-list query resolves.
 *
 * Per-source limit is 100 rows. Page size for the merged timeline is 25
 * with a "Load more" affordance. This is enough headroom for the
 * common case without ever returning a runaway result set.
 */

interface Props {
  entityType: 'contact' | 'company'
  entityId: string
}

type EventType = 'job' | 'quote' | 'invoice' | 'deal' | 'conversation' | 'note' | 'task' | 'deal_activity'

interface ActivityEvent {
  id: string
  type: EventType
  timestamp: string
  title: string
  subtitle?: string
  icon: LucideIcon
  link: string
}

const TYPE_META: Record<EventType, { label: string; icon: LucideIcon; color: string }> = {
  job:          { label: 'Jobs',          icon: Wrench,        color: 'var(--accent)' },
  quote:        { label: 'Quotes',        icon: FileText,      color: 'var(--info)' },
  invoice:      { label: 'Invoices',      icon: Receipt,       color: 'var(--positive)' },
  deal:         { label: 'Deals',         icon: TrendingUp,    color: 'var(--warning)' },
  conversation: { label: 'Conversations', icon: MessageSquare, color: 'var(--accent)' },
  note:         { label: 'Notes',         icon: StickyNote,    color: 'var(--ink-secondary)' },
  task:          { label: 'Tasks',         icon: CheckSquare,   color: 'var(--info)' },
  deal_activity: { label: 'Deal activity', icon: GitBranch,     color: 'var(--warning)' },
}

const PER_SOURCE_LIMIT = 100
const PAGE_SIZE = 25

// ─── Filter helpers (pure, exported for unit testing) ──────────────────────
export function filterEvents<T extends { type: string }>(events: T[], enabled: Record<string, boolean>): T[] {
  return events.filter(e => enabled[e.type])
}
export function toggleFilter<K extends string>(enabled: Record<K, boolean>, key: K): Record<K, boolean> {
  return { ...enabled, [key]: !enabled[key] }
}

// Filter chips are styled QUIETER than the page tabs (which own the accent
// colour) so the two rows don't read as duplicate navigation: neutral
// ghost/outline at rest, a muted neutral fill when active — never accent.
export function chipStyle(on: boolean): React.CSSProperties {
  return {
    padding: '3px 9px', borderRadius: 100, border: '1px solid',
    borderColor: on ? 'var(--border-default)' : 'var(--border-subtle)',
    background: on ? 'var(--surface-sunken)' : 'transparent',
    color: on ? 'var(--ink-primary)' : 'var(--ink-tertiary)',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
  }
}

export default function ActivityTimeline({ entityType, entityId }: Props) {
  const navigate = useNavigate()
  // Derived from TYPE_META so new source types are auto-included (no drift).
  const allEnabled = () => Object.fromEntries((Object.keys(TYPE_META) as EventType[]).map(k => [k, true])) as Record<EventType, boolean>
  const [enabled, setEnabled] = useState<Record<EventType, boolean>>(allEnabled)
  const [limit, setLimit] = useState(PAGE_SIZE)

  // For company mode, the join-through-contacts queries depend on the
  // contacts list. We fetch contact IDs once and feed them into the
  // per-source queries via react-query's `enabled` flag.
  const isCompany = entityType === 'company'
  const { data: companyContactIds = [] } = useQuery<string[]>({
    queryKey: ['activity-timeline-company-contacts', entityId],
    queryFn: async () => {
      const { data } = await supabase.from('contacts').select('id').eq('company_id', entityId)
      return (data || []).map((r: any) => r.id)
    },
    enabled: isCompany && !!entityId,
  })
  const contactScopeReady = !isCompany || (isCompany && companyContactIds !== undefined)

  const { data: jobs = [] } = useQuery({
    queryKey: ['activity-jobs', entityType, entityId, companyContactIds],
    queryFn: async () => {
      let q = supabase.from('field_jobs')
        .select('id, created_at, scheduled_date, status, address_line1, address_city, address_state, address_zip, job_number, total')
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      if (isCompany) {
        if (companyContactIds.length === 0) return []
        q = q.in('contact_id', companyContactIds)
      } else {
        q = q.eq('contact_id', entityId)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!entityId && contactScopeReady,
  })

  const { data: quotes = [] } = useQuery({
    queryKey: ['activity-quotes', entityType, entityId, companyContactIds],
    queryFn: async () => {
      let q = supabase.from('quotes')
        .select('id, created_at, sent_at, quote_number, status, total')
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      if (isCompany) {
        const ids = companyContactIds
        const orParts = [`company_id.eq.${entityId}`]
        if (ids.length > 0) orParts.push(`contact_id.in.(${ids.join(',')})`)
        q = q.or(orParts.join(','))
      } else {
        q = q.eq('contact_id', entityId)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!entityId && contactScopeReady,
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['activity-invoices', entityType, entityId, companyContactIds],
    queryFn: async () => {
      let q = supabase.from('invoices')
        .select('id, created_at, invoice_number, status, total')
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      if (isCompany) {
        const ids = companyContactIds
        const orParts = [`company_id.eq.${entityId}`]
        if (ids.length > 0) orParts.push(`contact_id.in.(${ids.join(',')})`)
        q = q.or(orParts.join(','))
      } else {
        q = q.eq('contact_id', entityId)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!entityId && contactScopeReady,
  })

  const { data: deals = [] } = useQuery({
    queryKey: ['activity-deals', entityType, entityId, companyContactIds],
    queryFn: async () => {
      let q = supabase.from('deals')
        .select('id, created_at, title, stage_name, status, value')
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      if (isCompany) {
        const ids = companyContactIds
        const orParts = [`company_id.eq.${entityId}`]
        if (ids.length > 0) orParts.push(`contact_id.in.(${ids.join(',')})`)
        q = q.or(orParts.join(','))
      } else {
        q = q.eq('contact_id', entityId)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!entityId && contactScopeReady,
  })

  const { data: conversations = [] } = useQuery({
    queryKey: ['activity-conversations', entityType, entityId, companyContactIds],
    queryFn: async () => {
      let q = supabase.from('conversations')
        .select('id, created_at, last_message_at, subject, status, channel')
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      if (isCompany) {
        if (companyContactIds.length === 0) return []
        q = q.in('contact_id', companyContactIds)
      } else {
        q = q.eq('contact_id', entityId)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!entityId && contactScopeReady,
  })

  const { data: notes = [] } = useQuery({
    queryKey: ['activity-notes', entityType, entityId],
    queryFn: async () => {
      const { data } = await supabase.from('entity_notes')
        .select('id, created_at, body, author_id')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      return data || []
    },
    enabled: !!entityId,
  })

  // sales_tasks — polymorphic (entity_type/entity_id), same shape as entity_notes.
  // Entity-scoped only (a deal-attached task shows on the deal, not here — matches notes).
  const { data: tasks = [] } = useQuery({
    queryKey: ['activity-tasks', entityType, entityId],
    queryFn: async () => {
      const { data } = await supabase.from('sales_tasks')
        .select('id, created_at, title, task_type, status, due_date, completed_at')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      return data || []
    },
    enabled: !!entityId,
  })

  // deal_activities — reuse the entity's already-fetched deals (source #4) rather
  // than re-deriving the company union. No deals → no activities, no error.
  const dealIds = (deals as any[]).map(d => d.id)
  const { data: dealActivities = [] } = useQuery({
    queryKey: ['activity-deal-activities', dealIds],
    queryFn: async () => {
      if (dealIds.length === 0) return []
      const { data } = await supabase.from('deal_activities')
        .select('id, deal_id, type, payload, created_at')
        .in('deal_id', dealIds)
        .order('created_at', { ascending: false })
        .limit(PER_SOURCE_LIMIT)
      return data || []
    },
    enabled: dealIds.length > 0,
  })

  const events: ActivityEvent[] = useMemo(() => {
    const all: ActivityEvent[] = []

    for (const j of jobs as any[]) {
      const addr = formatAddress(fromAddressPrefix(j))
      all.push({
        id: `job_${j.id}`,
        type: 'job',
        timestamp: j.created_at || j.scheduled_date || new Date(0).toISOString(),
        title: `${j.job_number || 'Job'} — ${(j.status || 'scheduled').replace(/_/g, ' ')}`,
        subtitle: [addr || null, typeof j.total === 'number' && j.total > 0 ? formatCurrency(j.total) : null].filter(Boolean).join(' · '),
        icon: TYPE_META.job.icon,
        link: `/app/jobs/${j.id}`,
      })
    }

    for (const q of quotes as any[]) {
      const cfg = getQuoteStatusConfig(q.status)
      all.push({
        id: `quote_${q.id}`,
        type: 'quote',
        timestamp: q.created_at || new Date(0).toISOString(),
        title: `${q.quote_number} — ${cfg.label}`,
        subtitle: typeof q.total === 'number' ? formatCurrency(q.total) : undefined,
        icon: TYPE_META.quote.icon,
        link: `/app/quotes/${q.id}`,
      })
    }

    for (const inv of invoices as any[]) {
      all.push({
        id: `invoice_${inv.id}`,
        type: 'invoice',
        timestamp: inv.created_at || new Date(0).toISOString(),
        title: `${inv.invoice_number} — ${inv.status || 'draft'}`,
        subtitle: typeof inv.total === 'number' ? formatCurrency(inv.total) : undefined,
        icon: TYPE_META.invoice.icon,
        link: `/app/invoices/${inv.id}`,
      })
    }

    for (const d of deals as any[]) {
      all.push({
        id: `deal_${d.id}`,
        type: 'deal',
        timestamp: d.created_at || new Date(0).toISOString(),
        title: d.title || 'Deal',
        subtitle: [d.stage_name || null, typeof d.value === 'number' ? formatCurrency(d.value) : null].filter(Boolean).join(' · '),
        icon: TYPE_META.deal.icon,
        link: `/app/deals`,
      })
    }

    for (const c of conversations as any[]) {
      all.push({
        id: `conv_${c.id}`,
        type: 'conversation',
        timestamp: c.last_message_at || c.created_at || new Date(0).toISOString(),
        title: c.subject || 'Conversation',
        subtitle: [c.channel || null, c.status || null].filter(Boolean).join(' · '),
        icon: TYPE_META.conversation.icon,
        link: `/app/inbox/${c.id}`,
      })
    }

    for (const n of notes as any[]) {
      const firstLine = (n.body || '').split('\n')[0]
      const preview = firstLine.length > 90 ? firstLine.slice(0, 87) + '…' : firstLine
      all.push({
        id: `note_${n.id}`,
        type: 'note',
        timestamp: n.created_at,
        title: 'Note added',
        subtitle: preview,
        icon: TYPE_META.note.icon,
        link: entityType === 'contact' ? `/app/contacts/${entityId}?tab=notes` : `/app/companies/${entityId}?tab=notes`,
      })
    }

    for (const t of tasks as any[]) {
      const due = t.due_date ? `Due ${formatRelativeDate(t.due_date)}` : null
      all.push({
        id: `task_${t.id}`,
        type: 'task',
        timestamp: t.created_at,
        title: t.title || 'Task',
        subtitle: [t.status || null, due, t.task_type || null].filter(Boolean).join(' · '),
        icon: TYPE_META.task.icon,
        link: entityType === 'contact' ? `/app/contacts/${entityId}?tab=tasks` : `/app/companies/${entityId}?tab=tasks`,
      })
    }

    const dealTitle = new Map((deals as any[]).map(d => [d.id, d.title]))
    for (const a of dealActivities as any[]) {
      const p = a.payload || {}
      let title: string
      if (a.type === 'stage_changed') title = `Stage changed: ${p.from_stage_name || '?'} → ${p.to_stage_name || '?'}`
      else if (a.type === 'created') title = 'Deal created'
      else if (a.type === 'won') title = 'Deal won'
      else if (a.type === 'lost') title = 'Deal lost'
      else title = a.type ? a.type.replace(/_/g, ' ') : 'Deal activity'
      all.push({
        id: `dealact_${a.id}`,
        type: 'deal_activity',
        timestamp: a.created_at,
        title,
        subtitle: dealTitle.get(a.deal_id) || undefined,
        icon: TYPE_META.deal_activity.icon,
        link: `/app/deals/${a.deal_id}`,
      })
    }

    all.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
    return all
  }, [jobs, quotes, invoices, deals, conversations, notes, tasks, dealActivities, entityType, entityId])

  const filtered = useMemo(() => filterEvents(events, enabled), [events, enabled])
  const visible = filtered.slice(0, limit)
  const hasMore = filtered.length > limit
  const allOn = (Object.keys(enabled) as EventType[]).every(k => enabled[k])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 2, color: 'var(--ink-tertiary)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <Filter style={{ width: 12, height: 12 }} /> Filter
        </span>
        <button type="button"
          onClick={() => setEnabled(allEnabled())}
          style={chipStyle(allOn)}>
          All
        </button>
        {(Object.keys(TYPE_META) as EventType[]).map(t => {
          const on = enabled[t]
          return (
            <button key={t} type="button"
              onClick={() => setEnabled(prev => toggleFilter(prev, t))}
              style={chipStyle(on)}>
              {TYPE_META[t].label}
            </button>
          )
        })}
      </div>

      {visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <MessageSquare style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No activity{filtered.length === 0 && events.length > 0 ? ' for the selected filters' : ' yet'}</p>
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 14 }}>
          <div style={{ position: 'absolute', left: 22, top: 14, bottom: 14, width: 1, background: 'var(--border-subtle)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visible.map(e => {
              const meta = TYPE_META[e.type]
              const Icon = e.icon
              return (
                <div key={e.id} onClick={() => navigate(e.link)}
                  style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 14, padding: '10px 14px 10px 0', cursor: 'pointer' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface)', border: `1px solid ${meta.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                    <Icon style={{ width: 13, height: 13, color: meta.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, transition: 'all 120ms' }}
                    onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--hover-bg)'; ev.currentTarget.style.borderColor = 'var(--accent-border)' }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = 'var(--surface)'; ev.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{e.title}</p>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }}>{formatRelativeDate(e.timestamp)}</span>
                    </div>
                    {e.subtitle && <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subtitle}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {hasMore && (
        <button type="button" onClick={() => setLimit(n => n + PAGE_SIZE)}
          style={{ padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', cursor: 'pointer', alignSelf: 'center' }}>
          Load more
        </button>
      )}
    </div>
  )
}
